<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { RouterLink, type RouteLocationRaw } from 'vue-router';
import { gaiaFontSize, gaiaFontWeight, gaiaFonts, gaiaNeutral } from '../../themes/tokens';

const props = defineProps({
  active: {
    type: Boolean,
    default: false,
  },
  /** Right-aligned count badge, e.g. the inbox count. */
  count: {
    type: [String, Number],
    default: undefined,
  },
  /** Pass true/false to render the expandable chevron; leave undefined for plain rows. */
  expanded: {
    type: Boolean,
    default: undefined,
  },
  /**
   * Nesting level: 0 = top-level row, 1 = child of an expanded group, 2 = child
   * of a nested group. Anything above 0 keeps the `child` class — GaiaSidebar's
   * collapsed rail hides nested rows through `:deep(.gaia-nav-item.child)`.
   */
  depth: {
    type: Number,
    default: 0,
  },
  /**
   * Route the row leads to. Set, the row renders as a real <a> (RouterLink) so
   * right-click "open in new tab", middle- and cmd-click work. Leave unset for
   * rows that only toggle (group parents).
   */
  to: {
    type: [String, Object] as PropType<RouteLocationRaw>,
    default: undefined,
  },
});

const ink = gaiaNeutral.ink;
const text = gaiaNeutral.text;
const textTertiary = gaiaNeutral.textTertiary;
const textMuted = gaiaNeutral.textMuted;
const hoverBg = gaiaNeutral.surfaceHover;
const activeBg = gaiaNeutral.surfaceActive;
const navFont = gaiaFonts.nav;
// This row's copy deliberately stays bare spans rather than GaiaText: the
// label and count inherit their color from the row's :hover/.active/.child
// states, and n-text pins its own color, which would freeze them. The tokens
// therefore reach them through v-bind. The class names are also load-bearing
// — GaiaSidebar hides .label/.count/.chev in the collapsed rail via :deep().
const medium = String(gaiaFontWeight.medium);
const bold = String(gaiaFontWeight.bold);
const semibold = String(gaiaFontWeight.semibold);
const iconSize = gaiaFontSize.subtitle;
// One indent step per level, starting from the row's own 10px padding — depth 1
// lands on the 38px the design has always used for group children.
const indent = computed(() => `${10 + 28 * props.depth}px`);
</script>

<template>
  <!-- Sidebar navigation row from mock 5a; expansion state is owned by the
       parent (bind `expanded` and toggle on click). -->
  <component
    :is="props.to ? RouterLink : 'div'"
    :to="props.to"
    class="gaia-nav-item"
    :class="{ active: props.active, child: props.depth > 0 }"
  >
    <n-flex v-if="$slots.icon" inline :size="0" :wrap="false" class="icon">
      <slot name="icon" />
    </n-flex>
    <span class="label"><slot /></span>
    <span v-if="props.count !== undefined" class="count">{{ props.count }}</span>
    <svg
      v-else-if="props.expanded !== undefined"
      class="chev"
      :class="{ open: props.expanded }"
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      stroke-width="1.5"
      stroke-linecap="round"
    >
      <path d="M4.5 2.5 8 6l-3.5 3.5" />
    </svg>
  </component>
</template>

<style scoped>
.gaia-nav-item {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 9px 10px;
  border-radius: 8px;
  font-family: v-bind(navFont);
  font-weight: v-bind(medium);
  color: v-bind(text);
  text-decoration: none;
  cursor: pointer;
}

.gaia-nav-item:hover {
  background: v-bind(hoverBg);
}

.gaia-nav-item.active {
  background: v-bind(activeBg);
  font-weight: v-bind(bold);
  color: v-bind(ink);
}

.icon {
  flex: none;
  font-size: v-bind(iconSize);
  color: v-bind(textTertiary);
}

.gaia-nav-item.active .icon {
  color: v-bind(ink);
}

.label {
  min-width: 0;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.count {
  flex: none;
  font-weight: v-bind(semibold);
  color: v-bind(text);
}

.chev {
  flex: none;
  color: v-bind(textMuted);
  transition: transform 0.15s;
}

.chev.open {
  transform: rotate(90deg);
}

.gaia-nav-item.child {
  padding: 7px 10px 7px v-bind(indent);
  font-weight: v-bind(medium);
  color: v-bind(textTertiary);
}

.gaia-nav-item.child:hover {
  color: v-bind(ink);
}

/* An active child row (e.g. the mock's highlighted "Playbooks") keeps the
   active ink/weight — the .child color must not win over .active. */
.gaia-nav-item.child.active {
  color: v-bind(ink);
  font-weight: v-bind(bold);
}
</style>
